/**
 * 사진 캡처 컴포넌트
 *
 * 카메라 촬영 또는 갤러리에서 답안지 이미지를 선택
 * - 모바일: 네이티브 카메라 앱 실행 (capture="environment")
 * - 데스크탑: 파일 선택기
 *
 * 이미지 선택 후 미리보기와 함께 "다시 찍기" / "채점하기" 버튼 표시
 *
 * Phase 5 개선사항:
 * - 이미지 자동 압축 (1MB 이하)
 * - 압축 결과 정보 표시
 */

import React, { useRef, useState } from 'react';
import { PixelButton } from '../common/PixelButton';
import {
  compressImage,
  formatCompressionResult,
  needsCompression,
  type CompressionResult,
} from '../../utils/imageCompression';

/**
 * PhotoCapture Props
 */
interface PhotoCaptureProps {
  /** 이미지 선택 후 채점 요청 콜백 */
  onSubmit: (image: File) => void;

  /** 취소 (모드 선택으로 돌아가기) 콜백 */
  onCancel: () => void;

  /** 로딩 중 여부 (Vision API 처리 중) */
  isLoading?: boolean;
}

/**
 * 최대 이미지 크기 (10MB)
 */
const MAX_IMAGE_SIZE = 10 * 1024 * 1024;

/**
 * 허용된 이미지 타입
 */
const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/heic'];

/**
 * 사진 캡처 컴포넌트
 *
 * @example
 * ```tsx
 * <PhotoCapture
 *   onSubmit={(image) => handleGradePage(image)}
 *   onCancel={() => setPageMode('mode_selection')}
 *   isLoading={isProcessing}
 * />
 * ```
 */
export const PhotoCapture: React.FC<PhotoCaptureProps> = ({
  onSubmit,
  onCancel,
  isLoading = false,
}) => {
  // 파일 입력 ref
  const fileInputRef = useRef<HTMLInputElement>(null);

  // 선택된 이미지 상태
  const [selectedImage, setSelectedImage] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Phase 5: 압축 상태
  const [isCompressing, setIsCompressing] = useState(false);
  const [compressionResult, setCompressionResult] = useState<CompressionResult | null>(null);

  /**
   * 파일 선택 핸들러
   *
   * Phase 5: 이미지 압축 로직 추가
   * - 큰 이미지는 자동으로 압축 (1MB 이하로)
   * - 압축 결과 메타데이터 저장
   */
  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // 파일 타입 검증
    if (!ALLOWED_TYPES.includes(file.type)) {
      setError('지원하지 않는 이미지 형식이에요. JPG, PNG만 가능해요.');
      return;
    }

    // 파일 크기 검증 (압축 전 10MB 제한)
    if (file.size > MAX_IMAGE_SIZE) {
      setError('이미지가 너무 커요. 10MB 이하로 선택해주세요.');
      return;
    }

    // 에러 초기화
    setError(null);

    // 이전 preview URL 정리
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
    }

    // 압축 결과 초기화
    setCompressionResult(null);

    try {
      // 압축 필요 여부 확인
      const shouldCompress = await needsCompression(file);

      let finalFile = file;
      let result: CompressionResult | null = null;

      if (shouldCompress) {
        // 압축 시작
        setIsCompressing(true);

        const compressed = await compressImage(file);
        finalFile = compressed.file;
        result = compressed.result;

        setCompressionResult(result);
        setIsCompressing(false);
      }

      // 새 이미지 설정
      setSelectedImage(finalFile);
      setPreviewUrl(URL.createObjectURL(finalFile));
    } catch (err) {
      setIsCompressing(false);
      setError('이미지 처리 중 오류가 발생했어요. 다시 시도해주세요.');
      console.error('[PhotoCapture] Compression error:', err);
    }
  };

  /**
   * 카메라 버튼 클릭 (파일 입력 트리거)
   */
  const handleCameraClick = () => {
    fileInputRef.current?.click();
  };

  /**
   * 다시 찍기 (이미지 초기화)
   */
  const handleRetake = () => {
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
    }
    setSelectedImage(null);
    setPreviewUrl(null);
    setError(null);
    setCompressionResult(null); // Phase 5: 압축 결과 초기화
    // 파일 입력 초기화
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  /**
   * 채점하기 (이미지 제출)
   */
  const handleSubmit = () => {
    if (selectedImage) {
      onSubmit(selectedImage);
    }
  };

  return (
    <div className="space-y-4">
      {/* 숨겨진 파일 입력 */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        onChange={handleFileChange}
        className="hidden"
      />

      {/* 압축 중 오버레이 (Phase 5) */}
      {isCompressing && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center">
          <div className="text-center">
            <div className="animate-pulse text-5xl mb-4">📦</div>
            <p className="font-pixel text-sm text-[#9bbc0f]">
              이미지 최적화 중...
            </p>
            <p className="font-pixel text-xs text-gray-500 mt-2">
              잠시만 기다려주세요
            </p>
          </div>
        </div>
      )}

      {/* 이미지 미선택 상태: 촬영 버튼 */}
      {!selectedImage && !isCompressing && (
        <>
          {/* 안내 메시지 */}
          <div className="text-center mb-4">
            <h3 className="font-pixel text-sm text-[#9bbc0f] mb-2">
              📷 답안지 사진 찍기
            </h3>
            <p className="font-pixel text-xs text-gray-400">
              풀이한 페이지를 깨끗하게 촬영해주세요
            </p>
          </div>

          {/* 촬영 영역 */}
          <button
            onClick={handleCameraClick}
            className={`
              w-full aspect-[4/3] rounded-lg
              bg-gray-800 hover:bg-gray-700
              border-4 border-dashed border-gray-600
              flex flex-col items-center justify-center
              transition-all duration-150
              active:scale-95
            `}
          >
            <span className="text-6xl mb-4">📸</span>
            <span className="font-pixel text-sm text-gray-400">
              터치해서 촬영하기
            </span>
            <span className="font-pixel text-xs text-gray-500 mt-2">
              또는 갤러리에서 선택
            </span>
          </button>

          {/* 촬영 가이드 */}
          <div className="bg-gray-800/50 rounded-lg p-3 border border-gray-700">
            <p className="font-pixel text-xs text-[#9bbc0f] mb-2">💡 촬영 팁</p>
            <ul className="font-pixel text-xs text-gray-400 space-y-1">
              <li>• 밝은 곳에서 촬영하세요</li>
              <li>• 답안지가 화면에 꽉 차게 찍으세요</li>
              <li>• 글씨가 선명하게 보이도록 해주세요</li>
            </ul>
          </div>

          {/* 취소 버튼 */}
          <div className="flex justify-center">
            <PixelButton
              variant="secondary"
              size="small"
              onClick={onCancel}
            >
              ← 돌아가기
            </PixelButton>
          </div>
        </>
      )}

      {/* 이미지 선택 상태: 미리보기 */}
      {selectedImage && previewUrl && (
        <>
          {/* 미리보기 */}
          <div className="relative rounded-lg overflow-hidden border-4 border-[#9bbc0f]">
            <img
              src={previewUrl}
              alt="답안지 미리보기"
              className="w-full h-auto max-h-[60vh] object-contain bg-black"
            />

            {/* 로딩 오버레이 */}
            {isLoading && (
              <div className="absolute inset-0 bg-black/70 flex items-center justify-center">
                <div className="text-center">
                  <div className="animate-pulse text-4xl mb-2">🔍</div>
                  <p className="font-pixel text-sm text-[#9bbc0f]">
                    AI가 답안을 읽는 중...
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* 이미지 정보 (Phase 5: 압축 결과 포함) */}
          <div className="text-center space-y-1">
            <p className="font-pixel text-xs text-gray-500">
              {selectedImage.name}
            </p>
            {compressionResult ? (
              <p className="font-pixel text-xs text-green-400">
                ✅ {formatCompressionResult(compressionResult)}
              </p>
            ) : (
              <p className="font-pixel text-xs text-gray-500">
                {(selectedImage.size / 1024 / 1024).toFixed(1)}MB
              </p>
            )}
          </div>

          {/* 버튼 그룹 */}
          <div className="grid grid-cols-2 gap-3">
            <PixelButton
              variant="secondary"
              size="medium"
              onClick={handleRetake}
              disabled={isLoading}
            >
              🔄 다시 찍기
            </PixelButton>
            <PixelButton
              variant="primary"
              size="medium"
              onClick={handleSubmit}
              disabled={isLoading}
            >
              ✨ 채점하기
            </PixelButton>
          </div>
        </>
      )}

      {/* 에러 메시지 */}
      {error && (
        <div className="bg-red-900/50 border border-red-500 rounded-lg p-3">
          <p className="font-pixel text-xs text-red-400 text-center">
            ⚠️ {error}
          </p>
        </div>
      )}
    </div>
  );
};

PhotoCapture.displayName = 'PhotoCapture';
